import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { IRootState } from '../../@types/redux';
import transformerFunctions from '../../store/transformer/functions';
import TabPanel from '../customComponents/tab/TabPanel';
import { IUserSession } from '../../@types/employee';
import TransformerDetail from './view/DetailView';
import { ITransformer } from '../../@types/model/transformer/transformer';
import PrimaryTabs from '../customComponents/tab/PrimaryTabs';
import PrimaryTab from '../customComponents/tab/PrimaryTab';
import TransformerGuaranteeListView from './guarantee/List';
import ProjectDialog from '../customComponentV2/dialog/ProjectDialog';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@material-ui/core';
import { ITransformerLpuReading } from '../../@types/model/transformer/reading/transformerLpuReading';
import { ITransformerReading } from '../../@types/model/transformer/reading/transformerReading';
import { ITransformerMunicipalityReading } from '../../@types/model/transformer/reading/transformerMunicipalityReading';
import TransformerReadingEntryView from './view/ReadingEntryView';
import AppFunctionsService from '../../services/appFunctionsService';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { StyledExpansionPanel } from '../customComponents/StyledExpansionPanel';
import MunicipalityReadingEntryView from './view/MunicipalityReadingEntryView';
import TransformerLpuReadingView from './view/LpuReadingView';
import TransformerLpuReadingEntryView from './view/LpuReadingEntryView';

interface ITransformerInfoProps {
    onClose ?: () => void;

    transformer ?: ITransformer;
    transformerLpuReadings : Array<ITransformerLpuReading>;
    transformerSpuReadings : Array<ITransformerReading>;
    transformerMunicipalityReadings : Array<ITransformerMunicipalityReading>;
    isLoading : boolean;
    session ?: IUserSession | null;
}

const TransformerInfo = (props : ITransformerInfoProps) : React.ReactElement => {
    const [tabIndex, setTabIndex] = useState<number>(0);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const handleClose = () : void => {
        transformerFunctions.setSelected();
        if(props.onClose) {
            props.onClose();
        }
    };

    const handleTabChange = (event : React.ChangeEvent<unknown>, value : number) : void => {
        setTabIndex(value);
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const spuHasPreviousLpuReadings = useMemo(() => {
        return props.transformer && props.transformer.MeterType === 'SPU'
            && props.transformerLpuReadings.length > 0;
    }, [
        props.transformer,
        props.transformerLpuReadings,
    ]);

    const spuHasPreviousMunicipalityReadings = useMemo(() => {
        return props.transformer && props.transformer.MeterType === 'SPU'
            && props.transformerMunicipalityReadings.length > 0;
    }, [
        props.transformer,
        props.transformerMunicipalityReadings,
    ]);

    const lpuHasPreviousSpuReadings = useMemo(() => {
        return props.transformer && props.transformer.MeterType === 'LPU'
            && props.transformerSpuReadings.length > 0;
    }, [
        props.transformer,
        props.transformerSpuReadings
    ]);

    const lpuHasPreviousMunicipalityReadings = useMemo(() => {
        return props.transformer && props.transformer.MeterType === 'LPU'
            && props.transformerMunicipalityReadings.length > 0;
    }, [
        props.transformer,
        props.transformerMunicipalityReadings
    ]);

    const municipalityHasPreviousSpuReadings = useMemo(() => {
        return props.transformer && props.transformer.MeterType === 'Municipality'
            && props.transformerSpuReadings.length > 0;
    }, [
        props.transformer,
        props.transformerSpuReadings
    ]);

    const municipalityHasPreviousLpuReadings = useMemo(() => {
        return props.transformer && props.transformer.MeterType === 'Municipality'
            && props.transformerLpuReadings.length > 0;
    }, [
        props.transformer,
        props.transformerLpuReadings
    ]);

    const spuReadings = useMemo(() => {
        return (
            <div key={'spuReadings'} className={'fdc flx1 pl20 pr20 pb20 pt10 bcg0 oya'}>
                {
                    !props.isLoading && props.transformerSpuReadings.length === 0 &&
                    <div>
                        <Typography className={'mb10'} color='inherit' variant='h5'>
                            No Reading Data Exists!
                        </Typography>
                    </div>
                }
                {
                    props.transformerSpuReadings.map(n => (
                        <Accordion key={n.ref.id} className={'wfill mb10'}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                {
                                    AppFunctionsService.formatDateTimeToDateOnly(n.CreatedOn)
                                }
                            </AccordionSummary>
                            <AccordionDetails className={'fdc flx1'}>
                                <TransformerReadingEntryView reading={n} />
                            </AccordionDetails>
                        </Accordion>
                    ))
                }
            </div>
        );
    }, [
        props.isLoading,
        props.transformerSpuReadings
    ]);

    const lpuReadings = useMemo<React.JSX.Element>(() => {

        // return <TransformerLpuReadingView session={props.session} />;

        return (
            <div key={'lpuReadings'} className={'fdc flx1 pl20 pr20 pb20 pt10 bcg0 oya'}>
                {
                    !props.isLoading && props.transformerLpuReadings.length === 0 &&
                    <div>
                        <Typography className={'mb10'} color='inherit' variant='h5'>
                            No Reading Data Exists!
                        </Typography>
                    </div>
                }
                {
                    props.transformerLpuReadings.map(n => (
                        <StyledExpansionPanel                             
                            key={n.id}
                            heading={AppFunctionsService.formatDateTimeToDateOnly(n.date)}
                            className={'wfill mb10'}
                        >
                            <TransformerLpuReadingEntryView reading={n} transformer={props.transformer}/>
                        </StyledExpansionPanel>
                    ))
                }
            </div>
        );
    }, [
        props.isLoading,
        props.transformerLpuReadings
    ]);

    const municipalityReadings = useMemo<React.JSX.Element>(() => {
        return (
            <div key={'municipalityReadings'} className={'fdc flx1 pl20 pr20 pb20 pt10 bcg0 oya'}>
                {
                    !props.isLoading && props.transformerMunicipalityReadings.length === 0 &&
                    <div>
                        <Typography className={'mb10'} color='inherit' variant='h5'>
                            No Reading Data Exists!
                        </Typography>
                    </div>
                }
                {
                    props.transformerMunicipalityReadings.map(n => (
                        <Accordion key={n.id} className={'wfill mb10'}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                {
                                    AppFunctionsService.formatDateTimeToDateOnly(n.createdOn)
                                }
                            </AccordionSummary>
                            <AccordionDetails className={'fdc flx1'}>
                                <MunicipalityReadingEntryView reading={n} />
                            </AccordionDetails>
                        </Accordion>
                    ))
                }
            </div>
        );
    }, [
        props.isLoading,
        props.transformerMunicipalityReadings
    ]);

    const tabHeaders = useMemo(() => {
        const tabLabels = [
            'DETAILS',
            'READINGS',
            'GUARANTEES',
        ];

        if (props.transformer?.MeterType === 'SPU') {
            if (spuHasPreviousLpuReadings) {
                tabLabels.push('PREVIOUS LPU READINGS');
            }
            if (spuHasPreviousMunicipalityReadings) {
                tabLabels.push('PREVIOUS MUNICIPALITY READINGS');
            }
        } else if (props.transformer?.MeterType === 'LPU') {
            if (lpuHasPreviousSpuReadings) {
                tabLabels.push('PREVIOUS SPU READINGS');
            }
            if (lpuHasPreviousMunicipalityReadings) {
                tabLabels.push('PREVIOUS MUNICIPALITY READINGS');
            }
        } else if (props.transformer?.MeterType === 'Municipality') {
            if (municipalityHasPreviousSpuReadings) {
                tabLabels.push('PREVIOUS SPU READINGS');
            }
            if (municipalityHasPreviousLpuReadings) {
                tabLabels.push('PREVIOUS LPU READINGS');
            }
        }

        return tabLabels;
    }, [
        props.transformer,
        spuHasPreviousLpuReadings,
        spuHasPreviousMunicipalityReadings,
        lpuHasPreviousSpuReadings,
        lpuHasPreviousMunicipalityReadings,
        municipalityHasPreviousSpuReadings,
        municipalityHasPreviousLpuReadings,
        municipalityReadings
    ]);

    const tabBodies = useMemo(() => {
        if (!props.transformer || !props.session) return [];

        const tabs = [
            <div className={'fdc flx1'}>
                <TransformerDetail
                    session={props.session}
                    transformer={props.transformer}
                    isLoading={props.isLoading}
                />
            </div>,
            <div className={'fdc flx1'}>
                {
                    props.transformer.MeterType === 'SPU' &&
                    <>
                        <Typography
                            variant={'h6'}
                            color={'primary'}
                            className={'pl20 bcg0'}
                        >
                            SPU Readings
                        </Typography>
                        {spuReadings}
                    </>
                }
                {
                    props.transformer.MeterType === 'LPU' &&
                    <>
                        <Typography
                            variant={'h6'}
                            color={'primary'}
                            className={'pl20 pt10 bcg0'}
                        >
                            LPU Readings
                        </Typography>
                        {lpuReadings}
                    </>
                }
                {
                    props.transformer.MeterType === 'Municipality' &&
                    <>
                        <Typography
                            variant={'h6'}
                            color={'primary'}
                            className={'pl20 pt10 bcg0'}
                        >
                            Municipality Readings
                        </Typography>
                        {municipalityReadings}
                    </>
                }
            </div>,
            <div className='fdc flx1'>
                <TransformerGuaranteeListView
                    session={props.session}
                    transformer={props.transformer}
                />
            </div>,
        ];

        if (props.transformer?.MeterType === 'SPU') {
            if (spuHasPreviousLpuReadings) {
                tabs.push(lpuReadings);
            }
            if (spuHasPreviousMunicipalityReadings) {
                tabs.push(municipalityReadings);
            }
        } else if (props.transformer?.MeterType === 'LPU') {
            if (lpuHasPreviousSpuReadings) {
                tabs.push(spuReadings);
            }
            if (lpuHasPreviousMunicipalityReadings) {
                tabs.push(municipalityReadings);
            }
        } else if (props.transformer?.MeterType === 'Municipality') {
            if (municipalityHasPreviousSpuReadings) {
                tabs.push(spuReadings);
            }
            if (municipalityHasPreviousLpuReadings) {
                tabs.push(lpuReadings);
            }
        }

        return tabs;
    }, [
        props.transformer,
        props.session,
        spuHasPreviousLpuReadings,
        spuHasPreviousMunicipalityReadings,
        lpuHasPreviousSpuReadings,
        lpuHasPreviousMunicipalityReadings,
        municipalityHasPreviousSpuReadings,
        municipalityHasPreviousLpuReadings,
        municipalityReadings,
        spuReadings,
        lpuReadings,
        municipalityReadings
    ]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <ProjectDialog
            isOpen={!!props.transformer}
            isLoadingCircular={props.isLoading}
            onClose={handleClose}
            fullScreen
            title={props.transformer ? `${props.transformer.EPNumber} - ${props.transformer.PoleNumber}` : ''}
        >
            <PrimaryTabs
                className={'bcp cw'}
                value={tabIndex}
                onChange={handleTabChange}
                aria-label={'Info Tabs'}
            >
                {
                    tabHeaders.map((tabLabel, index) => {
                        return (
                            <PrimaryTab
                                key={tabLabel}
                                label={tabLabel}
                                value={index}
                            />
                        );
                    })
                }
            </PrimaryTabs>
            <div className={'fdc flx1 hfill'}>
                {
                    tabBodies.map((tabBody, index) => {
                        if (index !== tabIndex) return <div/>;
                        return (
                            <TabPanel value={tabIndex} index={index}>
                                {tabBody}
                            </TabPanel>
                        );
                    })
                }
            </div>
        </ProjectDialog>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        transformer: state.transformer.transformer,
        isLoading: state.transformer.loading
            || state.transformer.loadingReadings
            || state.transformer.loadingLpuReadings
            || state.transformer.loadingMunicipalityReadings,
        transformerSpuReadings: state.transformer.readings,
        transformerLpuReadings: state.transformer.lpuReadings,
        transformerMunicipalityReadings: state.transformer.municipalityReadings,
        session: state.auth.session,
    };
};

export default connect(
    mapStateToProps,
)(TransformerInfo);
